import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { CheckCircle2, Loader2, XCircle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const ref = params.get('reference') || params.get('payment_id');
  const [state, setState] = useState('checking'); // checking | success | pending | failed | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!ref) { setState('error'); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/${ref}/status`);
        setInfo(data);
        if (data.status === 'succeeded') { setState('success'); return; }
        if (data.status === 'failed' || data.status === 'amount_mismatch') { setState('failed'); return; }
        if (data.status === 'auto_renew_failed') { setState('autorenew'); return; }
        tries += 1;
        if (tries < 12) { setState('pending'); setTimeout(poll, 5000); }
        else setState('pending');
      } catch { setState('error'); }
    };
    poll();
  }, [ref]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--c-bg)]" data-testid="payment-return">
      <div className="regen-card-elevated p-8 max-w-sm w-full text-center">
        {state === 'checking' || state === 'pending' ? (
          <>
            <Loader2 className="w-12 h-12 text-[var(--c-green-ink)] animate-spin mx-auto mb-4" />
            <h1 className="font-display font-black text-xl">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">{state === 'pending' ? "This can take a few seconds. We'll update automatically." : 'Please wait.'}</p>
          </>
        ) : state === 'success' ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-[var(--c-green-ink)] mx-auto mb-4" />
            <h1 className="font-display font-black text-2xl">You're in! 🎉</h1>
            <p className="text-sm text-muted-foreground mt-2">Your <b>{info?.program}</b> membership is active. Coach JP is preparing your plans.</p>
            <Button onClick={() => nav('/app/home')} data-testid="payment-continue-btn" className="mt-6 w-full bg-[var(--c-green)] text-[#081726] font-semibold">Continue to YourReGen</Button>
          </>
        ) : state === 'autorenew' ? (
          <AutoRenewFailed info={info} nav={nav} />
        ) : state === 'failed' ? (
          <>
            <XCircle className="w-14 h-14 text-[#FF5C5C] mx-auto mb-4" />
            <h1 className="font-display font-black text-xl">Payment not completed</h1>
            <p className="text-sm text-muted-foreground mt-2">Your payment didn't go through. You can try again.</p>
            <Button onClick={() => nav('/app/settings')} className="mt-6 w-full bg-[var(--c-film)]">Back to app</Button>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="font-display font-black text-xl">We couldn't confirm this yet</h1>
            <p className="text-sm text-muted-foreground mt-2">If you completed payment, your access will update shortly.</p>
            <Button onClick={() => nav('/app/home')} className="mt-6 w-full bg-[var(--c-film)]">Go to app</Button>
          </>
        )}
      </div>
    </div>
  );
}

// The card couldn't be saved for monthly charges: explain, charge nothing, and let the customer choose.
function AutoRenewFailed({ info, nav }) {
  const plan = info?.plan === 'regen_plus' ? 'plus' : 'protocol';
  return (
    <div data-testid="payment-autorenew-failed">
      <CreditCard className="w-14 h-14 text-[#FFB800] mx-auto mb-4" />
      <h1 className="font-display font-black text-xl">Automatic renewal couldn't be set up</h1>
      <p className="text-sm text-muted-foreground mt-2">
        Your card couldn't be used for automatic monthly charges — some cards, especially some debit and overseas cards,
        don't allow repeat payments. You have not been charged.
      </p>
      <p className="text-sm text-muted-foreground mt-2">You can pay for one month now and renew yourself when you're ready, or try a different card.</p>
      <Button onClick={() => nav(`/checkout?plan=${plan}&billing=monthly`)} data-testid="payment-one-month-btn" className="mt-6 w-full bg-[var(--c-green)] text-[#081726] font-semibold">Pay for one month instead</Button>
      <Button onClick={() => nav(`/checkout?plan=${plan}&billing=monthly`)} variant="outline" data-testid="payment-try-card-btn" className="mt-2 w-full">Try another card</Button>
    </div>
  );
}
